import axios from "axios";
import clubRequest from "../axios";
import { FileDownloadParams, FileDownloadResult } from "./type";
import { getValidAccessToken } from "@/services/session";

export const fileService = {
  /**
   * Download a file by fileName and optional bucket
   * @param params - File download parameters
   * @returns Promise with file blob and metadata
   */
  downloadFile: async (
    params: FileDownloadParams,
  ): Promise<FileDownloadResult> => {
    try {
      console.log("Attempting to download file:", params);

      // Use the generated API client
      const response = await clubRequest.fileList({
        fileName: params.fileName,
        bucket: params.bucket || "bucket",
      });

      console.log("API response:", response);
      console.log("Response type:", typeof response);

      // Try to create blob from response
      let blob: Blob;

      if (response instanceof Blob) {
        blob = response;
      } else if (response instanceof ArrayBuffer) {
        blob = new Blob([response], { type: "application/octet-stream" });
      } else if (typeof response === "string") {
        // If it's a base64 string or URL
        blob = new Blob([response], { type: "application/octet-stream" });
      } else {
        // Try to convert to blob
        blob = new Blob([JSON.stringify(response)], {
          type: "application/octet-stream",
        });
      }

      console.log("File download successful:", {
        size: blob.size,
        type: blob.type,
        fileName: params.fileName,
      });

      return {
        blob,
        fileName: params.fileName,
        originalName: params.fileName,
      };
    } catch (error: unknown) {
      console.error("Error downloading file:", error);
      console.error("Request params:", params);

      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response: { status: number; data: unknown; headers: unknown };
        };
        console.error("Response status:", axiosError.response.status);
        console.error("Response headers:", axiosError.response.headers);
        console.error("Response data:", axiosError.response.data);
      }

      throw error;
    }
  },

  /**
   * Get file URL for direct access (if supported by your API)
   * @param fileName - Name of the file
   * @param bucket - Optional bucket name
   * @returns File URL string
   */
  getFileUrl: (fileName: string, bucket?: string): string => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL;
    const bucketParam = bucket ? `&bucket=${bucket}` : "";
    return `${baseUrl}api/File?fileName=${fileName}${bucketParam}`;
  },

  /**
   * Download file and trigger browser download
   * @param params - File download parameters
   * @param customFileName - Optional custom name for downloaded file
   */
  downloadFileToBrowser: async (
    params: FileDownloadParams,
    customFileName?: string,
  ): Promise<void> => {
    try {
      const result = await fileService.downloadFile(params);

      // Create download link
      const url = window.URL.createObjectURL(result.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = customFileName || result.fileName;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file to browser:", error);
      throw error;
    }
  },
};

// --- helpers ---
export const sniffImageMime = async (blob: Blob): Promise<string | null> => {
  const head = await blob.slice(0, 16).arrayBuffer();
  const bytes = new Uint8Array(head);

  // JPEG
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff)
    return "image/jpeg";
  // PNG
  if (
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47 &&
    bytes[4] === 0x0d &&
    bytes[5] === 0x0a &&
    bytes[6] === 0x1a &&
    bytes[7] === 0x0a
  )
    return "image/png";
  // GIF87a / GIF89a
  if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46)
    return "image/gif";
  // WEBP (RIFF....WEBP)
  if (
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  )
    return "image/webp";
  return null;
};

export const toImageBlob = async (blob: Blob): Promise<Blob> => {
  const sniffed = await sniffImageMime(blob);
  return sniffed && blob.type !== sniffed
    ? new Blob([blob], { type: sniffed })
    : blob;
};

// Cache the Blob (not the object URL) so each consumer can create and revoke its own URL.
// Otherwise revoking in useGetImage/useMerchantImage invalidates the cached URL for others.
const merchantImageCache = new Map<string, Blob>();

function merchantImageCacheKey(fileName: string, bucket: string): string {
  return `${fileName}|${bucket}`;
}

export async function fetchMerchantImage(
  fileName: string,
  bucket: string = "merchantUser",
): Promise<string> {
  const cacheKey = merchantImageCacheKey(fileName, bucket);
  let blob = merchantImageCache.get(cacheKey);

  if (!blob) {
    const token = await getValidAccessToken();
    const url = `${
      import.meta.env.VITE_API_BASE_URL
    }api/File?fileName=${fileName}&bucket=${bucket}`;

    const response = await axios.get(url, {
      headers: {
        Accept: "application/json, text/plain, */*",
        Authorization: `Bearer ${token}`,
      },
      responseType: "blob", // <-- IMPORTANT: tells Axios to treat it as binary
    });

    // Some servers incorrectly set content-type to application/octet-stream.
    const contentType = response.headers["content-type"]?.startsWith("image/")
      ? response.headers["content-type"]
      : "image/jpeg";

    blob = new Blob([response.data], { type: contentType });
    merchantImageCache.set(cacheKey, blob);
  }

  // Return a new object URL each time so each consumer can revoke it on unmount
  return URL.createObjectURL(blob);
}
